import {
  SanityCollectionGridCollection,
  SanityCollectionSeoOverride,
  SanityCollectionSeoOverrideParentPageType,
  SanityCollectionTreeItem,
} from '@data/sanity/queries/types/modules'
import { useRouter } from 'next/router'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { MetadataContext } from './metadata-context'
import {
  getCollectionPageQuickFilterValues,
  getShopPageQuickFilterValues,
} from './product-collection'
import { ProductCollectionContext } from './product-collection-context'

export interface QuickFilterOption {
  value: string
  title: string
  featuredProductIds?: number[]
}

interface ProductCollectionQuickFilterContextProps {
  quickFilterEnabled: boolean
  quickFilterOptions: QuickFilterOption[]
  quickFilterSelectedValue: string | null
  setQuickFilterValue: (newValue: string | null) => void
}

interface ProductCollectionQuickFilterContextProviderProps {
  enabled: boolean
  collections: SanityCollectionTreeItem[]
  collection?: SanityCollectionGridCollection
  featuredCollectionIds?: string[]
  parentPageType?: SanityCollectionSeoOverrideParentPageType
  collectionSeoOverrides?: SanityCollectionSeoOverride[]
  childSlug?: string
  quickFilterBasePath?: string
  quickFilterAllPath?: string
  children: ReactNode
}

export const ProductCollectionQuickFilterContext =
  createContext<ProductCollectionQuickFilterContextProps>({
    quickFilterEnabled: false,
    quickFilterOptions: [],
    quickFilterSelectedValue: null,
    setQuickFilterValue: () => {},
  })

export const ProductCollectionQuickFilterContextProvider = ({
  enabled,
  collections,
  collection,
  featuredCollectionIds,
  parentPageType,
  collectionSeoOverrides,
  childSlug,
  quickFilterBasePath,
  quickFilterAllPath,
  children,
}: ProductCollectionQuickFilterContextProviderProps) => {
  const { setMetadataOverride } = useContext(MetadataContext)
  const { activeCollectionUrlParameters } = useContext(ProductCollectionContext)

  const router = useRouter()

  const quickFilterSelectedValue = useMemo(() => {
    const urlParameter = activeCollectionUrlParameters.find((urlParameter) => {
      return urlParameter.name === 'collection'
    })
    const urlParameterValue = urlParameter?.value as string | null | undefined

    return childSlug ?? urlParameterValue ?? null
  }, [activeCollectionUrlParameters, childSlug])

  const quickFilterOptions = useMemo(() => {
    if (collection) {
      return getCollectionPageQuickFilterValues(
        collections,
        collection._id,
        featuredCollectionIds,
      )
    }

    return getShopPageQuickFilterValues(collections, featuredCollectionIds)
  }, [collection, collections, featuredCollectionIds])

  const setQuickFilterValue = useCallback(
    (newValue: string | null) => {
      if (quickFilterBasePath && newValue) {
        router.push(`${quickFilterBasePath}/${newValue}`)
      }

      if (!newValue) {
        router.push(quickFilterAllPath ?? '/')
      }
    },
    [quickFilterBasePath, quickFilterAllPath, router],
  )

  // Child collection metadata
  useEffect(() => {
    const childCollection = quickFilterSelectedValue
      ? collections.find((childCollection) => {
          return childCollection.slug === quickFilterSelectedValue
        })
      : null
    let childCollectionSeoOverride = childCollection?.seo ?? null

    if (childCollection && collectionSeoOverrides) {
      const collectionSeoOverride = collectionSeoOverrides.find(
        (collectionSeoOverride) => {
          if (
            collectionSeoOverride.childCollection.slug.current !==
            quickFilterSelectedValue
          ) {
            return false
          }

          if (collection) {
            return collectionSeoOverride.parentPage._id === collection._id
          }

          return collectionSeoOverride.parentPage._type === parentPageType
        },
      )

      if (collectionSeoOverride?.seo) {
        childCollectionSeoOverride = collectionSeoOverride.seo
      }
    }

    setMetadataOverride(childCollectionSeoOverride)

    return () => {
      setMetadataOverride(null)
    }
  }, [
    collection,
    collections,
    collectionSeoOverrides,
    quickFilterSelectedValue,
    parentPageType,
    setMetadataOverride,
  ])

  return (
    <ProductCollectionQuickFilterContext.Provider
      value={{
        quickFilterEnabled: enabled,
        quickFilterOptions,
        quickFilterSelectedValue,
        setQuickFilterValue,
      }}
    >
      {children}
    </ProductCollectionQuickFilterContext.Provider>
  )
}
